<template>
  <div class="panel">
    <div class="section">
      <div class="title">{{ $t('editor.design.templates') }}</div>
      <div class="items">
        <div
          v-for="template in templates"
          :key="template.id"
          class="templates item"
          :class="{ selected: template.id === resume.template.id }"
        >
          <div class="item-title">{{ template.name }}</div>
          <div class="image-container">
            <div class="image" :style="`background-image: url(${template.thumbnailUrl})`" />
          </div>
          <div v-if="template.id === resume.template.id" class="selected footer">
            <check class="icon" />
            {{ $t('words.selected') }}
          </div>
          <div
            v-else
            class="secondary-button"
            @click="
              () => {
                selectedTemplate = template.id;
              }
            "
          >
            {{ $t('editor.design.select') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import check from '@/assets/icons/checked.svg';
import { mapGetters } from 'vuex';

export default {
  name: 'TemplatePanel',
  components: {
    check,
  },
  props: {
    resume: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedTemplate: null,
    };
  },

  computed: {
    ...mapGetters(['templates', 'activeColorPalette']),
  },

  watch: {
    selectedTemplate(id) {
      if (!id) return;
      this.$store.dispatch('resumes.template.change', {
        resumeId: this.resume.id,
        templateId: id,
      });
      const variables = this.colorPalette();
      this.$store.dispatch('resumes.template.edit', {
        resumeId: this.resume.id,
        template: { variables },
      });
    },
  },

  created() {
    let colorPalette = this.resume.template.palettes.find(
      p =>
        p.variables[0].value === this.resume.template.variables[0].value &&
        p.variables[1].value === this.resume.template.variables[1].value &&
        p.variables[2].value === this.resume.template.variables[2].value
    );
    if (!colorPalette) {
      colorPalette = this.resume.template.palettes[0];
    }
    const variables = [
      { name: 'color1', value: colorPalette.variables[0].value, type: 'color' },
      { name: 'color2', value: colorPalette.variables[1].value, type: 'color' },
      { name: 'color3', value: colorPalette.variables[2].value, type: 'color' },
    ];
    this.$store
      .dispatch('resumes.template.edit', {
        resumeId: this.resume.id,
        template: { variables },
      })
      .then(() => {
        this.$store.dispatch('resume.template.setColor', variables);
      });
  },
  methods: {
    colorPalette() {
      let palette = this.resume.template.palettes.find(
        p =>
          p.variables[0].value === this.activeColorPalette[0].value &&
          p.variables[1].value === this.activeColorPalette[1].value &&
          p.variables[2].value === this.activeColorPalette[2].value
      );
      if (!palette) {
        palette = this.resume.template.palettes[0];
      }
      return palette.variables;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

$title-height: 32px;
$light-grey-color: grey;

.panel {
  border-left: solid 1px #d0d0d0;
  left: 200px !important;
  height: calc(100% - 30px);
  overflow-y: scroll;
  overflow-x: initial;
  $title-height: 30px;
  padding: 8px 24px;
  border-right: none;
  width: 320px !important;
  min-width: 260px !important;
  max-width: 700px !important;
  flex-shrink: 0;

  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-right: solid 1px #d0d0d0;
    &:first-child {
      .title {
        border-top: none;
      }
    }
    .title {
      height: $title-height;
      line-height: $title-height;
      text-transform: uppercase;
      font-weight: bold;
      color: colors.$darkest-grey-color;
      border-bottom: solid 1px #d0d0d0;
      font-size: 14px;
      box-sizing: border-box;
      user-select: none;
      width: 280px;
      letter-spacing: 1.3px;
    }

    .items {
      height: calc(100vh - #{app.$topbar-height} - #{$title-height});
      overflow: scroll;
      width: 100%;
      &:focus {
        outline: none;
      }

      .item {
        position: relative;
        font-size: 14px;
        color: #7d7d7d;
        padding: 11px 0;
        box-sizing: border-box;
        user-select: none;
        width: 280px;
        border-top: solid 1px #d0d0d0;
        cursor: default;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 0 auto;
        &:focus {
          outline: none;
        }

        &:first-child {
          margin-top: 11px;
          border-top: solid 1px transparent;
        }

        .secondary-button {
          margin-top: 14px;
          width: 189px;
          font-weight: 600;
          transition: box-shadow 0.3s;
          box-sizing: border-box;
          &:hover {
            box-shadow: 0 0 0 4px colors.$hover-create-button-shadow;
          }
        }

        .item-title {
          box-sizing: border-box;
          color: inherit;
          width: 100%;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          margin-bottom: 9px;
          text-transform: capitalize;
        }
        .image-container {
          display: flex;
          width: 100%;
          .image {
            height: 211px;
            width: 100%;
            background-position: center;
            background-size: contain;
            background-repeat: no-repeat;
          }
        }

        &.selected {
          background: colors.$light-blue-color;
          color: colors.$white-color;
          width: 100%;
          padding: 11px 22px;

          & + .item {
            border-top: solid 1px transparent;
          }

          .footer {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 16px;
            margin-top: 14px;
            width: 100%;
            font-size: 11px;
            text-transform: uppercase;
            font-weight: 600;
            .icon {
              width: 11px;
              height: 11px;
              margin-right: 7px;
            }
          }
        }
        .colors {
          box-sizing: border-box;
          display: flex;
          justify-content: space-between;
          width: 100%;
          .color {
            text-align: center;
            margin-top: 15px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            .circle {
              height: 48px;
              width: 48px;
              border-radius: 50%;
              margin-bottom: 4px;
            }
            .code {
              font-size: 9px;
            }
          }
        }
      }
    }
  }
}
</style>
